import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getEvents } from '../services/firestoreService';
import { useSEO } from '../hooks/useSEO';
import { debugEventsInFirebase } from '../utils/debugEvents';
import { generateStructuredData } from '../services/seoService';
import { useAuth } from '../contexts/AuthContext';

const HomePage = () => {
  const { currentUser } = useAuth();
  const isLoggedIn = !!currentUser;
  const [featuredEvents, setFeaturedEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  // SEO optimization for home page
  useSEO({
    structuredData: [
      generateStructuredData('Organization'),
      generateStructuredData('WebSite')
    ]
  });

  // Load upcoming events from Firebase
  useEffect(() => {
    const loadUpcomingEvents = async () => {
      try {
        setLoading(true);

        // Debug events in Firebase
        await debugEventsInFirebase();

        // Get upcoming events from Firebase (same as Events page)
        const upcomingData = await getEvents(20, true);

        // Take the first 3 upcoming events for the homepage
        const featured = upcomingData.slice(0, 3).map(event => {
          console.log('🖼️ Event image data:', {
            id: event.id,
            title: event.title,
            imageUrl: event.imageUrl,
            image: event.image,
            allFields: Object.keys(event)
          });

          return {
            id: event.id,
            title: event.title,
            image: event.imageUrl || event.image || '/apn-logos/APN-blue (1).png', // Use Firebase imageUrl first, then fallback
            date: event.startDate?.toDate ? event.startDate.toDate() : new Date(event.startDate),
            location: event.location
          };
        });

        setFeaturedEvents(featured);
      } catch (error) {
        console.error('Error loading upcoming events for homepage:', error);
        setFeaturedEvents([]);
      } finally {
        setLoading(false);
      }
    };

    loadUpcomingEvents();
  }, []);

  const latestNews = [
    {
      id: 2,
      title: 'APN Q1 NEWSLETTER',
      image: '/images/news/q1-newsletter-full.png',
      category: 'News',
      excerpt: 'Check out our Q1 newsletter for the latest updates and industry insights.'
    }
  ];

  const sponsors = [
    {
      id: 1,
      name: 'Hilton Global Associates',
      logo: '/images/sponsors/hga-logo.jpg',
      url: 'https://www.hiltonglobalassociates.com/'
    }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-apn-blue text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            {/* APN Logo */}
            <div className="flex justify-center mb-6">
              <img
                src={`${process.env.PUBLIC_URL}/apn-logos/APN-Tag-white.jpg`}
                alt="APN Logo"
                className="h-24"
              />
            </div>
            <h1 className="text-4xl font-bold mb-4">Accelerated Professionals Network</h1>
            <p className="text-xl mb-8">A community of finance professionals collaborating to create a more seamless M&A transaction process.</p>
            <div className="flex justify-center space-x-4">
              <Link to="/about/membership" className="btn-primary bg-white text-apn-blue hover:bg-gray-100">
                Join APN
              </Link>
              <Link to="/events" className="btn-secondary bg-transparent text-white border-white hover:bg-apn-light-blue hover:border-transparent">
                Upcoming Events
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Membership Tiers Banner (for non-members) */}
      {!isLoggedIn && (
        <section className="bg-gray-100 py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h2 className="text-2xl font-bold mb-4">Membership Tiers</h2>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div className="bg-white p-4 rounded shadow">
                  <h3 className="font-bold text-lg">Tier 1</h3>
                  <p className="text-apn-blue font-bold">$75/year</p>
                  <Link to="/about/membership" className="text-sm text-apn-blue hover:underline">Learn More</Link>
                </div>
                <div className="bg-white p-4 rounded shadow">
                  <h3 className="font-bold text-lg">Tier 2</h3>
                  <p className="text-apn-blue font-bold">$125/year</p>
                  <Link to="/about/membership" className="text-sm text-apn-blue hover:underline">Learn More</Link>
                </div>
                <div className="bg-white p-4 rounded shadow">
                  <h3 className="font-bold text-lg">Tier 3</h3>
                  <p className="text-apn-blue font-bold">$175/year</p>
                  <Link to="/about/membership" className="text-sm text-apn-blue hover:underline">Learn More</Link>
                </div>
                <div className="bg-white p-4 rounded shadow">
                  <h3 className="font-bold text-lg">Tier 4</h3>
                  <p className="text-apn-blue font-bold">$225/year</p>
                  <Link to="/about/membership" className="text-sm text-apn-blue hover:underline">Learn More</Link>
                </div>
                <div className="bg-white p-4 rounded shadow">
                  <h3 className="font-bold text-lg">Tier Vet</h3>
                  <p className="text-apn-blue font-bold">$75/year</p>
                  <p className="text-xs text-gray-500">For protected veterans</p>
                  <Link to="/about/membership" className="text-sm text-apn-blue hover:underline">Learn More</Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Featured Events */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold mb-6">Upcoming Events</h2>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[1, 2, 3].map(i => (
                <div key={i} className="bg-white rounded-lg shadow overflow-hidden animate-pulse">
                  <div className="h-48 bg-gray-200"></div>
                  <div className="p-4">
                    <div className="h-4 bg-gray-200 rounded mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded mb-2 w-3/4"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : featuredEvents.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {featuredEvents.map(event => (
                <div key={event.id} className="bg-white rounded-lg shadow overflow-hidden">
                  <Link to={`/events/${event.id}`} className="block h-48 bg-white relative overflow-hidden">
                    <div className="h-full w-full overflow-hidden flex items-center justify-center bg-white">
                      <img
                        src={(() => {
                          // Handle different image URL formats
                          if (!event.image) {
                            return `${process.env.PUBLIC_URL}/apn-logos/APN-blue (1).png`;
                          }
                          // If it's a full URL (Firebase Storage), use it directly
                          if (event.image.startsWith('http')) {
                            return event.image;
                          }
                          // If it's a relative path, add PUBLIC_URL
                          return `${process.env.PUBLIC_URL}${event.image}`;
                        })()}
                        alt={event.title}
                        className="h-full w-full object-contain transition-transform duration-300 hover:scale-105"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = `${process.env.PUBLIC_URL}/apn-logos/APN-blue (1).png`;
                        }}
                      />
                    </div>
                  </Link>
                  <div className="p-4">
                    <h3 className="font-bold text-lg mb-2">{event.title}</h3>
                    <p className="text-gray-600 mb-2">
                      {event.date instanceof Date
                        ? event.date.toLocaleDateString('en-US', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })
                        : event.date
                      } • {event.location}
                    </p>
                    <Link to={`/events/${event.id}`} className="text-apn-blue hover:underline">
                      View Details
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-600 mb-4">No upcoming events at this time.</p>
              <Link to="/events" className="text-apn-blue hover:underline">
                View all events
              </Link>
            </div>
          )}

          {!loading && featuredEvents.length > 0 && (
            <div className="mt-6 text-center">
              <Link to="/events" className="btn-primary">
                View All Events
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Latest News */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold mb-6">Latest News</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {latestNews.map(news => (
              <div key={news.id} className="bg-white rounded-lg shadow overflow-hidden">
                <Link to={`/news/${news.id}`} className="block h-48 bg-white relative overflow-hidden">
                  <div className="h-full w-full overflow-hidden flex items-center justify-center bg-white">
                    <img
                      src={`${process.env.PUBLIC_URL}${news.image}`}
                      alt={news.title}
                      className="h-full w-full object-contain transition-transform duration-300 hover:scale-105"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = `${process.env.PUBLIC_URL}/apn-logos/APN-blue (1).png`;
                      }}
                    />
                  </div>
                </Link>
                <div className="p-4">
                  <span className="inline-block bg-apn-blue text-white text-xs px-2 py-1 rounded mb-2">
                    {news.category}
                  </span>
                  <h3 className="font-bold text-lg mb-2">{news.title}</h3>
                  <p className="text-gray-600 mb-2">{news.excerpt}</p>
                  <Link to={`/news/${news.id}`} className="text-apn-blue hover:underline">
                    Read More
                  </Link>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-6 text-center">
            <Link to="/news" className="btn-primary">
              View All News
            </Link>
          </div>
        </div>
      </section>

      {/* Sponsors Corner - Hidden until sponsors are secured */}
      {false && (
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold mb-6">Sponsors Corner</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {sponsors.map(sponsor => (
              <a
                key={sponsor.id}
                href={sponsor.url}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white p-4 rounded-lg shadow flex items-center justify-center hover:shadow-md transition-shadow"
              >
                <div className="h-24 flex items-center justify-center">
                  <img
                    src={`${process.env.PUBLIC_URL}${sponsor.logo}`}
                    alt={sponsor.name}
                    className="h-16 object-contain"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = `${process.env.PUBLIC_URL}/apn-logos/APN-blue (1).png`;
                    }}
                  />
                </div>
              </a>
            ))}
            {/* Empty sponsor slots */}
            {[...Array(3)].map((_, index) => (
              <div
                key={`empty-${index}`}
                className="bg-white p-4 rounded-lg shadow flex items-center justify-center border-2 border-dashed border-gray-300"
              >
                <div className="text-center text-gray-400">
                  <p>Become a Sponsor</p>
                  <Link to="/about/sponsorship" className="text-sm text-apn-blue hover:underline">
                    Learn More
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Call to Action */}
      <section className="bg-apn-blue text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* APN Logo */}
          <div className="flex justify-center mb-6">
            <img
              src={`${process.env.PUBLIC_URL}/apn-logos/APN-Tag-SQ-white.jpg`}
              alt="APN Logo Square"
              className="h-20"
            />
          </div>
          <h2 className="text-3xl font-bold mb-4">Ready to Join the Network?</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Connect with finance professionals, access exclusive resources, and accelerate your career with APN.
          </p>
          <Link to="/register" className="btn-primary bg-white text-apn-blue hover:bg-gray-100">
            Become a Member Today
          </Link>
        </div>
      </section>

      {/* Member Benefits */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold mb-6 text-center">Member Benefits</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow text-center">
              <div className="text-apn-blue mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-2">Networking</h3>
              <p className="text-gray-600">
                Connect with finance professionals across various sectors including private equity, investment banking, accounting, and more.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow text-center">
              <div className="text-apn-blue mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-2">Job Opportunities</h3>
              <p className="text-gray-600">
                Access exclusive job postings and career opportunities through our dedicated job board for finance professionals.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow text-center">
              <div className="text-apn-blue mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-2">Events</h3>
              <p className="text-gray-600">
                Attend exclusive networking events, workshops, and conferences designed for finance professionals.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
